import { Injectable } from '@angular/core';

import { TwcPopupRef, TwcPopupService } from '@topcon/twc-angular';

import { EntryPointService, ToastService, TranslationService } from '@mirage-projects/cw-commons';
import {
    Layer,
    PcvizCollageWebFileBrowserListResult,
    PcvizLayer,
    PcvizProject,
    PcvizZipJobAdapter,
    Project,
} from '@mirage-projects/cw-models';
import { DataAccessPcvizService, JobPcvizService, UtilsPcvizService } from '@mirage-projects/cw-pcviz-core';

import { MessageDialogOptions, MessageDialogService } from '../message-dialog';
import { PopupFileBrowserComponent } from './popup-file-browser.component';

export interface FileBrowserOptions {
    title?: string;
    fileOptions?: {
        showDelete?: boolean;
        showSize?: boolean;
        showType?: boolean;
    };
}

export interface FileBrowserFilters {
    level: number;
    acceptNames?: string[];
    exceptNames?: string[];
    folder?: string | RegExp;
    replaceNames?: any;
}

/**
 * This service implements the business logic related to the file browser.
 */
@Injectable({
    providedIn: 'root',
})
export class PopupFileBrowserService {
    // TODO: We set a large value for the pagination because right now we get the complete listing because it is filtered and paginated in the view. This should be changed in the future to a more reasonable value when back supports filtering.
    /**
     * Number of items per page used when calling the service that returns the list
     */
    readonly PAGE_SERVICE_SIZE = 10000;
    /**
     * Number of items per page used in view
     */
    readonly PAGE_VIEW_SIZE = 20;

    readonly EXTENSIONS = {
        REPORT: ['.txt', '.html'],
        TIN: ['.ply'],
        IMAGE: ['.jpg', '.JPG', '.png', '.PNG'],
    };

    fileBrowser = this.dataAccessPcvizService.createCollageWebFileBrowser();
    searchText = '';
    fileBrowserLevel = 0;
    breadcrumb = '';
    isLoading = false;
    visibleFileList: PcvizCollageWebFileBrowserListResult[] = [];
    numberOfVisibleElements = this.PAGE_VIEW_SIZE;
    fileList: PcvizCollageWebFileBrowserListResult[] = [];
    downloadingFile: PcvizCollageWebFileBrowserListResult | null = null;
    filters: any[] = [];
    catalogObject: any;
    catalogObjectType!: string;
    existNextPage = false;
    /**
     * Indicates the folders to show
     */
    folders: string[] = [];
    options: FileBrowserOptions | undefined;
    refPopupFileBrowser: TwcPopupRef | undefined;

    constructor(
        private popupService: TwcPopupService,
        private dataAccessPcvizService: DataAccessPcvizService,
        private utilsPcvizService: UtilsPcvizService,
        private toastService: ToastService,
        private jobPcvizService: JobPcvizService,
        private translationService: TranslationService,
        private entryPointService: EntryPointService,
        private messageDialogService: MessageDialogService
    ) {}

    /**
     * Open the popup with the file browser and initialize the service properties
     * @param folders Directories to be displayed in the browser
     * @param filters Filters to be applied to the files returned by the service
     * @param catalogObject Catalog object: Project or Layer
     * @param catalogObjectType Catalog object: 'projects' or 'layers'
     * @param options
     */
    openPopupFileBrowser(
        folders: string[],
        filters: any[],
        catalogObject: PcvizProject | PcvizLayer | Project | Layer,
        catalogObjectType: string,
        options?: FileBrowserOptions
    ): void {
        this.folders = folders;
        this.filters = filters;
        this.catalogObject = catalogObject;
        this.catalogObjectType = catalogObjectType;
        this.options = options;
        this.refPopupFileBrowser = this.popupService.open(PopupFileBrowserComponent);
        this.initialize();
    }

    /**
     * Close the file browser popup
     */
    closePopupFileBrowse(): void {
        this.refPopupFileBrowser?.close();
    }

    /**
     * Navigate to the parent directory and update the breadcrumb
     */
    goBack(): void {
        this.fileBrowserLevel--;
        this.clearFilter();
        if (this.fileBrowserLevel === 0 && this.folders.length) {
            this.listFolders();
        } else {
            const parentPath = this.fileBrowser.getCurrentFolder().substring(0, this.fileBrowser.getCurrentFolder().lastIndexOf('/'));
            this.listFolders([parentPath]);
        }
        this.breadcrumb = this.breadcrumb.substring(0, this.breadcrumb.lastIndexOf('/'));
    }

    /**
     * Filter the files to be displayed to the user (visibleFileList)
     * from the complete list of files (fileList) according to the search text (searchText)
     */
    filter(): void {
        if (!this.searchText) {
            this.visibleFileList = this.fileList.slice(0, this.numberOfVisibleElements);
            this.existNextPage = this.visibleFileList.length < this.fileList.length;
            return;
        }
        const text = this.searchText.toLowerCase();
        const filteredList = this.fileList.filter((file) => {
            const fileName = file.nameReplaced?.toLowerCase() ?? file.name.toLowerCase();
            return fileName.match(text);
        });

        this.visibleFileList = filteredList.slice(0, this.numberOfVisibleElements);
        this.existNextPage = this.visibleFileList.length < filteredList.length;
    }

    /**
     * Displays the following PAGE_VIEW_SIZE files
     */
    nextPage(): void {
        this.numberOfVisibleElements += this.PAGE_VIEW_SIZE;
        this.filter();
    }

    /**
     * Accesses the directory passed by parameters. Get all the files in this directory and update the breadcrumb
     * @param file
     */
    async openDirectory(file: PcvizCollageWebFileBrowserListResult): Promise<void> {
        if (!this.isDirectory(file)) {
            return;
        }

        this.fileBrowserLevel++;
        this.clearFilter();
        await this.listFolders([file.path]);
        this.breadcrumb += file.nameReplaced ? `/${file.nameReplaced}` : `/${file.name}`;
    }

    /**
     * @param file
     * @returns returns true if the input file is Directory
     */
    isDirectory(file: PcvizCollageWebFileBrowserListResult): boolean {
        return file.kind === 'D';
    }

    /**
     * @param file
     * @returns returns true if the input file is File
     */
    isFile(file: PcvizCollageWebFileBrowserListResult): boolean {
        return file.kind === 'F';
    }

    /**
     * Downloads de input file (or directory)
     * @param file
     * @param event
     */
    download(file: PcvizCollageWebFileBrowserListResult, event: Event): void {
        event.stopPropagation();
        if (this.isFile(file)) {
            this.downloadFile(file);
        }
        if (this.isDirectory(file)) {
            this.downloadDirectory(file);
        }
    }

    /**
     * Removes de input file and refresh the file lists
     * @param file
     * @param event
     */
    remove(file: PcvizCollageWebFileBrowserListResult, event: Event): void {
        event.stopPropagation();
        const options: Partial<MessageDialogOptions> = {
            headerText: 'FILE_BROWSER.CONFIRM_DELETE',
            contentText: 'FILE_BROWSER.DELETE_FILE_CONFIRM_MSG',
            buttonsList: [this.messageDialogService.getCancelButtonConfig(), this.messageDialogService.getOkButtonConfig()],
        };

        this.messageDialogService.showWarning(options).then(async (button) => {
            if (button.id === 'ok') {
                try {
                    this.isLoading = true;
                    await this.fileBrowser.delete(file);
                    this.removeInList(this.fileList, file);
                    this.removeInList(this.visibleFileList, file);
                } catch (e) {
                    this.toastService.error('FILE_BROWSER.ERROR_DELETE');
                } finally {
                    this.isLoading = false;
                }
            }
        });
    }

    getGenericFilters(): FileBrowserFilters[] {
        return [
            {
                level: 0,
                acceptNames: ['process', 'process_data', 'cc_output', 'downloads'],
                replaceNames: {
                    process: this.translationService.translateWithDefault('FILE_BROWSER.INPUT_FOLDER', 'input'),
                    process_data: this.translationService.translateWithDefault('FILE_BROWSER.METADATA_FOLDER', 'CC metadata'),
                    cc_output: this.translationService.translateWithDefault('FILE_BROWSER.DELIVERABLES_FOLDER', 'CC deliverables'),
                    downloads: this.translationService.translateWithDefault('FILE_BROWSER.DOWNLOADS_FOLDER', 'downloads'),
                },
            },
            {
                level: 1,
                folder: 'process',
                exceptNames: ['cc_output'],
            },
            {
                level: 3,
                folder: /^process\/cc_output\/.*dsm$/,
                exceptNames: ['tiles'],
            },
        ];
    }

    private removeInList(list: PcvizCollageWebFileBrowserListResult[], file: PcvizCollageWebFileBrowserListResult): void {
        const i = list.indexOf(file);
        list.splice(i, 1);
    }

    private async downloadFile(file: PcvizCollageWebFileBrowserListResult): Promise<void> {
        this.downloadingFile = file;
        try {
            await this.fileBrowser.download(file);
        } catch (e) {
            this.toastService.error('FILE_BROWSER.ERROR_DOWNLOAD');
        } finally {
            this.downloadingFile = null;
        }
    }

    private async downloadDirectory(file: PcvizCollageWebFileBrowserListResult): Promise<void> {
        const zipJobAdapter = this.jobPcvizService.createZipJobAdapter();

        // avoid download some folders
        if (file.name !== 'cc_output') {
            zipJobAdapter.addExcludedPath('cc_output' + '/*');
        }
        zipJobAdapter.addExcludedPath('tiles/*');
        zipJobAdapter.addExcludedPath('*/tiles/*');

        const catalogObjectName = this.catalogObject?.getName?.() ?? this.catalogObject.name;
        const catalogObjectId = this.catalogObject?.getId?.() ?? this.catalogObject.id;
        const config = {
            jobName: `${catalogObjectName} - ${this.translationService.translate('FILE_BROWSER.DOWNLOAD_FOLDER')}`,
            catalogId: this.entryPointService.getEntryPoint(this.catalogObjectType, catalogObjectId),
            files: [file.path + '/*'],
            zipName: file.path.replace(/\//g, '-'),
        };
        const job = zipJobAdapter.getCompleteJob(config);
        this.downloadingFile = file;
        try {
            await job.create(true);
            // TODO: If the viewer is scan, we have to call WorkflowServiceScan.addAvoidReloadScans
            job.on('finish', async () => {
                const zipFileName = zipJobAdapter.getZipFileName(config);
                const fileBrowserDownload = this.dataAccessPcvizService.createCollageWebFileBrowser();
                this.utilsPcvizService.configureDriver(fileBrowserDownload, this.entryPointService.getEntryPointsForCore());
                const files = await fileBrowserDownload.list(
                    this.catalogObject,
                    this.catalogObjectType,
                    (this.jobPcvizService.getZipJobAdapter() as PcvizZipJobAdapter).ZIP_TEMP_PATH,
                    undefined,
                    false
                );
                files.forEach((f: any) => {
                    if (f.name === zipFileName) {
                        fileBrowserDownload.download(f);
                    }
                });
                this.downloadingFile = null;
                // TODO: If the viewer is scan, we have to call WorkflowServiceScan.removeAvoidReloadScans
            });
        } catch (e) {
            this.toastService.error('FILE_BROWSER.ERROR_DOWNLOAD');
            this.downloadingFile = null;
        }
    }

    private clearFilter(): void {
        this.searchText = '';
        this.fileBrowser.clearCurrentFiles();
    }

    private initialize(): void {
        this.fileBrowserLevel = 0;
        this.breadcrumb = '';
        this.searchText = '';
        this.isLoading = false;
        this.downloadingFile = null;
        this.existNextPage = false;
        this.numberOfVisibleElements = this.PAGE_VIEW_SIZE;
        this.utilsPcvizService.configureDriver(this.fileBrowser, this.entryPointService.getEntryPointsForCore());
        this.fileBrowser.setPageSize(this.PAGE_SERVICE_SIZE);
        this.clearFilter();
        this.listFolders();
    }

    /**
     *  We recursively call listFolder to get the list of all directories.
     *  The fileList will be overwritten but the final result will be the accumulated result
     *  of all the files since we are passing the accumulate=true parameter to the list function.
     */
    private async listFolders(folders?: string[]): Promise<void> {
        if (!folders) {
            folders = this.folders.slice();
        }
        const folder = folders?.pop() as string;
        this.fileList = await this.list(this.catalogObject, this.catalogObjectType, folder, undefined, true);
        if (folders.length) {
            await this.listFolders(folders);
            return;
        }
        this.numberOfVisibleElements = this.PAGE_VIEW_SIZE;
        this.visibleFileList = this.fileList.slice(0, this.numberOfVisibleElements);
        this.existNextPage = this.visibleFileList.length < this.fileList.length;
    }

    private async list(
        objectData: any,
        objectType: string,
        folder: string,
        next?: boolean,
        returnAccumulate?: boolean
    ): Promise<PcvizCollageWebFileBrowserListResult[]> {
        this.isLoading = true;
        try {
            const fileList = await this.fileBrowser.list(objectData, objectType, folder, next, returnAccumulate);

            return this.filterFileList(fileList);
        } catch (e) {
            this.toastService.error('FILE_BROWSER.ERROR_LIST');
        } finally {
            this.isLoading = false;
        }
        return [];
    }

    private filterFileList(fileList: PcvizCollageWebFileBrowserListResult[]): PcvizCollageWebFileBrowserListResult[] {
        if (!this.filters.length) {
            return fileList;
        }

        const currentFolder = this.fileBrowser.getCurrentFolder();
        const currentFilter = this.filters.find(
            (filter) => filter.level === this.fileBrowserLevel && (this.fileBrowserLevel === 0 || RegExp(filter.folder).exec(currentFolder))
        );
        if (currentFilter) {
            if (currentFilter.acceptNames) {
                fileList = fileList.filter((element) => currentFilter.acceptNames.includes(element.name));
            }
            if (currentFilter.exceptNames) {
                fileList = fileList.filter((element) => !currentFilter.exceptNames.includes(element.name));
            }
            if (currentFilter.replaceNames) {
                fileList.forEach((element) => {
                    if (currentFilter.replaceNames[element.name]) {
                        element.nameReplaced = currentFilter.replaceNames[element.name];
                    }
                });
            }
        }

        return fileList;
    }
}
